import { checkIfUserTypeIsAllowed } from '../utils/access-control'
import { TipoUsuario } from '@/core/enums/tipo-usuario'
import { Idioma, IdiomaInfo } from '@tecsinapse-org/portal-menu-kit'
import {
  useApolloClient,
  useLazyQuery,
  useMutation,
  useQuery
} from '@apollo/client'
import {
  GetMenuCountNotificationsQuery,
  GetMenuUsuarioQuery,
  Idioma as IdiomaGQL,
  MarcarNotificacoesComoVisualizadasMutation,
  MarcarNotificacoesComoVisualizadasMutationVariables,
  MenuUsuario,
  NotificacoesMenuQuery,
  SalvarUsuarioLogadoMutationMutation,
  SalvarUsuarioLogadoMutationMutationVariables,
  Usuario
} from '@/core/types/graphql'
import { mudarLinguaUsuario } from '../data/mudar-lingua-usuario'
import { getMenuUsuarioGql } from '../data/menu'
import { usuarioLogadoQueryGql } from '../data/usuario-logado'
import { marcarNotificacoesComoVisualizadasGql } from '../data/marcar-notificacoes-como-visualizadas'
import { useCallback, useContext, useEffect, useState } from 'react'
import {
  getMenuCountNotificationsGql,
  getMenuNotificationsGql
} from '../data/menu-notifications'
import { KeycloakContext } from '../utils/keycloak-context'
import { i18n } from '../../i18n'

export const useApp = (currentUser: Usuario) => {
  const [mobileSearchText, setMobileSearchText] = useState('')
  const [searchMode, setSearchMode] = useState(false)

  const client = useApolloClient()

  const [mudarLinguaUsuarioMutation] = useMutation<
    SalvarUsuarioLogadoMutationMutation,
    SalvarUsuarioLogadoMutationMutationVariables
  >(mudarLinguaUsuario, {
    refetchQueries: [getMenuUsuarioGql, usuarioLogadoQueryGql]
  })

  const [marcarNotificacoesComoVisualizadas] = useMutation<
    MarcarNotificacoesComoVisualizadasMutation,
    MarcarNotificacoesComoVisualizadasMutationVariables
  >(marcarNotificacoesComoVisualizadasGql, {
    refetchQueries: [getMenuCountNotificationsGql]
  })

  const { data: { menuUsuarioLogado } = {}, loading } =
    useQuery<GetMenuUsuarioQuery>(getMenuUsuarioGql)

  const [loadMenuNotifications] = useLazyQuery<NotificacoesMenuQuery>(
    getMenuNotificationsGql
  )

  const { data: { countNotificacoesNaoLidas = 0 } = {} } =
    useQuery<GetMenuCountNotificationsQuery>(getMenuCountNotificationsGql, {
      pollInterval: 60000
    })

  const { logout } = useContext(KeycloakContext)

  const { idioma, tipo, id } = currentUser

  useEffect(() => {
    const idiomaAtual = Idioma[idioma?.id as keyof typeof Idioma]
    i18n.activate(IdiomaInfo[idiomaAtual].language)
  }, [idioma])

  const { menus: _menus, ...menuDataProps } = (menuUsuarioLogado ??
    {}) as MenuUsuario

  const menus = JSON.parse(_menus ?? '{}')
  const menuData = { menus, ...menuDataProps }

  const showOptionSuperAdmin = checkIfUserTypeIsAllowed(tipo, [
    TipoUsuario.TECSINAPSE
  ])

  const changeUserLanguage = useCallback(
    async (idioma: IdiomaGQL) => {
      await mudarLinguaUsuarioMutation({
        variables: { id, idioma }
      })
      location.reload()
    },
    [mudarLinguaUsuarioMutation, id]
  )

  const customOptions = showOptionSuperAdmin
    ? [
        {
          label: 'SuperAdmin',
          path: '/superAdmin'
        }
      ]
    : undefined

  return {
    loading,
    logout,
    loadMenuNotifications,
    countNotificacoesNaoLidas,
    customOptions,
    changeUserLanguage,
    menuData,
    marcarNotificacoesComoVisualizadas,
    menuDataProps,
    mobileSearchText,
    setMobileSearchText,
    searchMode,
    setSearchMode,
    client
  }
}
